import React, { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import dayjs from 'dayjs';

const GOLD = '#C8922A';
const BD2 = '#EAE6DF';
const BG = '#FAF8F5';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const RED = '#C0392B';
const GRN = '#2E7D4F';
const JPY_CNY_FALLBACK = 0.043;

const FAIL_CODES = {
  PRICE_EXCEEDED: { label: '价格超限', cls: 'price', desc: '最终价超过您设定的授权价，引擎按规停止' },
  BID_FAILED: { label: '出价失败', cls: 'engine', desc: '引擎提交出价时发生错误，平台将全额退还保证金并赔付' },
  LOT_ENDED: { label: '已结拍', cls: 'engine', desc: '引擎到达时拍卖已结束' },
  SESSION_EXPIRED: { label: '会话失效', cls: 'engine', desc: '拍卖行账号会话异常，平台将全额赔付' },
};

function formatJPY(val) {
  if (val == null) return '--';
  return '¥' + Number(val).toLocaleString('ja-JP');
}

function TimelineItem({ item }) {
  const dotStyle = item.dotType === 'ok'
    ? { background: 'rgba(46,125,79,.12)', color: GRN }
    : item.dotType === 'exec'
    ? { background: 'rgba(200,146,42,.12)', color: GOLD }
    : { background: 'rgba(26,22,18,.06)', color: INK3 };

  return (
    <div style={{ display: 'flex', gap: 12, paddingBottom: 14, position: 'relative' }}>
      {/* Connector line */}
      <div style={{
        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, ...dotStyle,
      }}>
        &#x2022;
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{item.label}</div>
        <div style={{ fontFamily: 'monospace', fontSize: 11, color: INK4 }}>{item.time}</div>
        {item.detail && <div style={{ fontSize: 12, color: INK3, marginTop: 3 }}>{item.detail}</div>}
      </div>
    </div>
  );
}

export default function BidResult() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [JPY_CNY, setJpyCny] = useState(JPY_CNY_FALLBACK);
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = data?.rate; if (r && r > 0) setJpyCny(Number(r));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchMandate = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/mandates/${id}`);
        setMandate(data.data || data);
      } catch (err) {
        message.error(t('common.failed'));
      } finally {
        setLoading(false);
      }
    };
    fetchMandate();
  }, [id]);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '80px 0' }}><Spin size="large" /></div>;
  }

  if (!mandate) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0', color: INK3 }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>📋</div>
        <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 18 }}>{t('common.noData')}</div>
        <button onClick={() => navigate('/mandates')} style={{
          marginTop: 20, padding: '10px 24px', background: GOLD, color: '#fff', border: 'none', borderRadius: 3,
          fontSize: 14, cursor: 'pointer',
        }}>{t('mandates.title')}</button>
      </div>
    );
  }

  const isWon = mandate.status === 'won';
  const isLost = mandate.status === 'lost';
  const finalPrice = mandate.final_price || mandate.final_price_jpy || 0;
  const maxBid = mandate.max_bid_jpy || 0;
  const marketRef = mandate.market_ref_price || Math.round(maxBid * 1.15);
  const savings = isWon ? maxBid - finalPrice : 0;
  const savingsPct = isWon && marketRef > 0 ? Math.round((marketRef - finalPrice) / marketRef * 100) : 0;
  const failCode = mandate.fail_code || 'PRICE_EXCEEDED';
  const failInfo = FAIL_CODES[failCode] || FAIL_CODES.PRICE_EXCEEDED;

  const lotTitle = mandate.lot_title || '--';
  const sessionName = mandate.session_name || mandate.house_name || '--';
  const mandateNo = mandate.mandate_no || mandate.id || '--';
  const emoji = { watch: '⌚', jewelry: '💍', bag: '👜' }[mandate.category] || '📦';

  // Build timeline
  const timeline = mandate.timeline || (isWon ? [
    { dotType: 'info', label: '委托提交', time: mandate.created_at ? dayjs(mandate.created_at).format('MM-DD HH:mm') : '--', detail: `授权价 ${formatJPY(maxBid)} · 保证金冻结` },
    { dotType: 'info', label: '委托审核通过', time: mandate.approved_at ? dayjs(mandate.approved_at).format('MM-DD HH:mm') : '--', detail: '运营确认，引擎加入调度队列' },
    { dotType: 'ok', label: '页面预热完成', time: '--', detail: '引擎打开拍品页，MutationObserver注入成功' },
    { dotType: 'info', label: '价格监听中', time: '--', detail: '共检测到多次价格更新' },
    { dotType: 'exec', label: '引擎出价执行', time: '--', detail: `结拍前 30 秒 · 出价 ${formatJPY(finalPrice)}` },
    { dotType: 'ok', label: '竞拍成功', time: mandate.won_at ? dayjs(mandate.won_at).format('MM-DD HH:mm') : '--', detail: `成交价 ${formatJPY(finalPrice)} · 节省 ${formatJPY(savings)}` },
  ] : [
    { dotType: 'info', label: '委托提交', time: mandate.created_at ? dayjs(mandate.created_at).format('MM-DD HH:mm') : '--', detail: `授权价 ${formatJPY(maxBid)} · 保证金冻结` },
    { dotType: 'ok', label: '页面预热完成', time: '--', detail: '引擎就位，开始监听价格' },
    { dotType: 'info', label: '价格监听', time: '--', detail: '共检测到多次价格更新' },
    { dotType: 'exec', label: '出价时机检查', time: '--', detail: `当前价 ${formatJPY(finalPrice)} >= 授权价 ${formatJPY(maxBid)}，引擎停止` },
    { dotType: 'info', label: '保证金已解冻', time: '--', detail: '保证金已退回余额' },
  ]);

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: BG, color: INK, fontSize: 14, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px' }}>
        {/* Result card */}
        <div style={{
          background: '#fff', border: `1px solid ${BD2}`, borderRadius: 6,
          overflow: 'hidden', boxShadow: '0 2px 16px rgba(26,22,18,.08)', marginBottom: 24,
        }}>
          {/* Hero */}
          <div style={{
            padding: '36px 32px', textAlign: 'center', borderBottom: `1px solid ${BD2}`,
            background: isWon
              ? 'linear-gradient(180deg,rgba(46,125,79,.06) 0%,transparent 100%)'
              : 'linear-gradient(180deg,rgba(192,57,43,.04) 0%,transparent 100%)',
          }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>{isWon ? '🏆' : '😔'}</div>
            <div style={{
              fontFamily: 'var(--serif,serif)', fontSize: 26, fontWeight: 700, marginBottom: 8,
              color: isWon ? GRN : RED,
            }}>
              {isWon ? t('bidResult.won') : t('bidResult.lost')}
            </div>
            <div style={{ fontSize: 14, color: INK3, lineHeight: 1.7 }}>
              {emoji} {lotTitle}<br />{sessionName}
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '24px 32px' }}>
            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 20 }}>
              {(isWon ? [
                { label: t('auction.currentBid') + ' JPY', val: formatJPY(finalPrice), color: GRN },
                { label: t('common.cny'), val: `¥${Math.round(finalPrice * JPY_CNY).toLocaleString()}` },
                { label: t('auction.myBid'), val: formatJPY(maxBid), color: GOLD },
                { label: t('mandates.status'), val: mandateNo, small: true },
              ] : [
                { label: t('auction.currentBid'), val: formatJPY(finalPrice), color: RED },
                { label: t('auction.myBid'), val: formatJPY(maxBid) },
                { label: t('auction.bidAmount'), val: formatJPY(finalPrice - maxBid), color: RED },
                { label: t('mandates.status'), val: mandateNo, small: true },
              ]).map((item, i) => (
                <div key={i} style={{
                  padding: '13px 0', borderBottom: `1px solid ${BD2}`,
                  ...(i % 2 === 0 ? { paddingRight: 24, borderRight: `1px solid ${BD2}` } : { paddingLeft: 24 }),
                }}>
                  <div style={{ fontSize: 11, color: INK4, letterSpacing: 1, fontFamily: 'monospace', marginBottom: 5 }}>{item.label}</div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: item.small ? 13 : 16, fontWeight: 600,
                    color: item.color || INK,
                  }}>{item.val}</div>
                </div>
              ))}
            </div>

            {/* Won: Savings banner */}
            {isWon && savings > 0 && (
              <div style={{
                background: 'rgba(46,125,79,.06)', border: '1px solid rgba(46,125,79,.15)',
                borderRadius: 4, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20,
              }}>
                <div>
                  <div style={{ fontSize: 13, color: GRN, fontWeight: 500 }}>{t('bidResult.won')}</div>
                  <div style={{ fontSize: 11, color: GRN, opacity: 0.8 }}>{t('bidResult.wonDesc')} {savingsPct}%</div>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: GRN, marginLeft: 'auto' }}>
                  {formatJPY(savings)} JPY
                </div>
              </div>
            )}

            {/* Lost: Fail reason */}
            {isLost && (
              <div style={{
                background: 'rgba(192,57,43,.05)', border: '1px solid rgba(192,57,43,.15)',
                borderRadius: 4, padding: '16px 20px', marginBottom: 20,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: RED, marginBottom: 6 }}>
                  <span style={{
                    display: 'inline-block', fontFamily: 'monospace', fontSize: 11, padding: '2px 8px', borderRadius: 3, marginRight: 6,
                    background: failInfo.cls === 'price' ? 'rgba(192,57,43,.1)' : 'rgba(200,146,42,.1)',
                    color: failInfo.cls === 'price' ? RED : GOLD,
                  }}>{failInfo.label}</span>
                  {t('bidResult.lost')}
                </div>
                <div style={{ fontSize: 13, color: INK2, lineHeight: 1.7 }}>
                  {mandate.fail_reason || failInfo.desc}
                </div>
                {failCode !== 'PRICE_EXCEEDED' && (
                  <div style={{ marginTop: 8, fontSize: 12, color: GRN }}>
                    由于引擎原因未能出价，平台将全额退还保证金并支付赔付金。
                  </div>
                )}
              </div>
            )}

            {/* Timeline */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: INK3, letterSpacing: 1, marginBottom: 12 }}>{t('auction.bidHistory')}</div>
              <div style={{ position: 'relative' }}>
                {timeline.map((item, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    {i < timeline.length - 1 && (
                      <div style={{
                        position: 'absolute', left: 9, top: 20, bottom: 0, width: 1, background: BD2, zIndex: 0,
                      }} />
                    )}
                    <TimelineItem item={item} />
                  </div>
                ))}
              </div>
            </div>

            {/* CTA buttons */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {isWon && (
                <>
                  <button onClick={() => navigate(mandate.order_id ? `/orders/${mandate.order_id}/payment` : '/orders')} style={{
                    flex: 1, minWidth: 120, padding: 13, borderRadius: 3, fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', textAlign: 'center', border: 'none', background: GOLD, color: '#fff',
                  }}>{t('bidResult.goPayment')}</button>
                  {/* 仅当 admin 已录入检品评级时才显示入口 */}
                  {mandate.order_id && mandate.inspection_grade && (
                    <button onClick={() => navigate(`/orders/${mandate.order_id}/inspection`)} style={ctaOutlineStyle}>{t('inspection.title')}</button>
                  )}
                  <button onClick={() => navigate('/auction-hub')} style={ctaOutlineStyle}>{t('bidResult.viewMore')}</button>
                </>
              )}
              {isLost && (
                <>
                  {mandate.lot_id && (
                    <button onClick={() => navigate(`/lots/${mandate.lot_id}`)} style={{
                      flex: 1, minWidth: 120, padding: 13, borderRadius: 3, fontSize: 14, fontWeight: 600,
                      cursor: 'pointer', textAlign: 'center', border: 'none', background: GOLD, color: '#fff',
                    }}>{t('common.retry')}</button>
                  )}
                  <button onClick={() => navigate('/auction-hub')} style={{
                    flex: 1, minWidth: 120, padding: 13, borderRadius: 3, fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', textAlign: 'center', border: 'none', background: INK, color: '#fff',
                  }}>{t('bidResult.viewMore')}</button>
                  <button onClick={() => navigate('/session-calendar')} style={ctaOutlineStyle}>{t('session.title')}</button>
                </>
              )}
              <button onClick={() => navigate('/mandates')} style={ctaOutlineStyle}>{t('mandates.title')}</button>
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div style={{
          background: '#fff', border: `1px solid ${BD2}`, borderRadius: 6,
          padding: '20px 24px', boxShadow: '0 2px 8px rgba(26,22,18,.05)',
        }}>
          <div style={{ fontFamily: 'var(--serif,serif)', fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t('recommend.hotLots')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            {[
              { icon: '⌚', name: 'Rolex Daytona 116500LN 白盘', price: '¥168万起 · Timeless 4月场' },
              { icon: '⌚', name: 'AP Royal Oak 15500ST 蓝面', price: '¥198万起 · Eco Auc 5月场' },
              { icon: '⌚', name: 'PP Nautilus 5711A 钢款', price: '¥285万起 · JWA 4月场' },
            ].map((rec, i) => (
              <div key={i} onClick={() => navigate('/auction-hub')} style={{
                border: `1px solid ${BD2}`, borderRadius: 4, padding: 14,
                cursor: 'pointer', transition: 'all .14s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 2px 8px rgba(200,146,42,.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = BD2; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ fontSize: 28, marginBottom: 8 }}>{rec.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: INK, marginBottom: 4, lineHeight: 1.4 }}>{rec.name}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: GOLD }}>{rec.price}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const ctaOutlineStyle = {
  flex: 1, minWidth: 120, padding: 13, borderRadius: 3, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', textAlign: 'center', border: `1px solid #D8D2C8`, background: '#fff', color: '#3D3830',
};
